// src/components/owner/OwnerTopNav.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { AppBar, Box, Button, Toolbar, Typography } from '@mui/material';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';

const THEME_KEY = 'ui_theme_mode';

function getInitialMode() {
  try {
    const saved = localStorage.getItem(THEME_KEY);
    if (saved === 'dark' || saved === 'light') return saved;
  } catch {}
  if (document.documentElement.classList.contains('dark')) return 'dark';
  if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

function hasOwnerToken() {
  try {
    const keys = Object.keys(localStorage);
    return keys.some((k) =>
      /owner.*token|token.*owner|cmfm_owner_token|ownerToken/i.test(k) &&
      localStorage.getItem(k)
    );
  } catch {
    return false;
  }
}

export default function OwnerTopNav({ active }) {
  const loc = useLocation();
  const navigate = useNavigate();
  const [mode, setMode] = useState(getInitialMode());

  // Sync with <html class="dark">
  useEffect(() => {
    const updateFromDom = () => {
      const isDark = document.documentElement.classList.contains('dark');
      const next = isDark ? 'dark' : 'light';
      setMode(next);
      try { localStorage.setItem(THEME_KEY, next); } catch {}
    };
    updateFromDom();
    const ob = new MutationObserver(updateFromDom);
    ob.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => ob.disconnect();
  }, []);

  const toggleTheme = () => {
    const isDark = document.documentElement.classList.toggle('dark');
    const next = isDark ? 'dark' : 'light';
    setMode(next);
    try { localStorage.setItem(THEME_KEY, next); } catch {}
  };

  const tabs = useMemo(() => ([
    { label: 'OVERVIEW',       href: '/owner',          key: 'home'     },
    { label: 'ACADEMIC AREAS', href: '/owner/subjects', key: 'subjects' },
    { label: 'INSTITUTIONS',   href: '/owner/colleges', key: 'colleges' },
  ]), []);

  const isActive = (k, href) =>
    (active && active === k) || (href && loc.pathname.startsWith(href));

  const isLoginRoute = /\/owner\/login$/.test(loc.pathname);
  const loggedIn = hasOwnerToken();
  const showMenus = loggedIn && !isLoginRoute; // << hide tabs on login & when not signed-in

  return (
    <AppBar position="static" color="default" enableColorOnDark>
      <Toolbar sx={{ gap: 1 }}>
        <Box sx={{ flexGrow: 0, mr: 1.5, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.05 }}>
            MedEdTek
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: { xs: 'none', sm: 'block' }, lineHeight: 1.1 }}
          >
            Institutional Administration
          </Typography>
        </Box>

        {showMenus && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {tabs.map(t => (
              <Button
                key={t.key}
                component={RouterLink}
                to={t.href}
                variant={isActive(t.key, t.href) ? 'contained' : 'outlined'}
                size="small"
              >
                {t.label}
              </Button>
            ))}
            <Button
              onClick={() => navigate('/owner/logout')}
              color="error"
              variant="text"
              size="small"
              sx={{ ml: 1 }}
            >
              LOGOUT
            </Button>
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {/* Dark mode toggle */}
        <Button
          onClick={toggleTheme}
          size="small"
          variant="outlined"
          aria-label="Toggle dark mode"
          title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          startIcon={mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
        >
          {mode === 'dark' ? 'Light' : 'Dark'}
        </Button>
      </Toolbar>
    </AppBar>
  );
}
